/**
 * Shared Marvin stone-map pattern logic for preset + full (Thinkspace) builders.
 *
 * Single source for:
 *   • Style-size → G1–G4 stone index maps (BUILDER_GEM_MAPS)
 *   • Alternating accent group slots (ALT_ACCENT_GROUPS)
 *   • Pattern kind → [G1, G2, G3, G4] gem color values (gemsForPattern)
 *   • 310/311 four-corner Alternating / Alternating X rules
 *
 * Consumers: assets/preset-3d-options.js, assets/sj-gemstone-enhancer.js
 * @see MarvinLive/builder_gem_map.py
 */
(function (global) {
  'use strict';

  /**
   * Marvin builder gem maps (style-size → stone labels per G1–G4).
   * Pattern → G1–G4 colors are style-specific (not a naive [C,white,C,white]).
   *
   * 123-10 ALT green: G1+G3+G4 green, G2 white
   * 245-10 ALT green: G2+G3 green, G1+G4 white
   * X (both):         G3+G4 accent, G1+G2 white
   */
  var GEM_MAPS = {
    '123-10': {
      G1: [1, 5, 7, 11, 15, 17],
      G2: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20],
      G3: [9, 19],
      G4: [3, 13],
    },
    '123-12': {
      G1: [2, 6, 10, 12, 14, 16, 18, 20, 22],
      G2: [1, 3, 5, 7, 11, 13, 17, 21],
      G3: [4, 8],
      G4: [15, 9],
    },
    '123-12x10': {
      G1: [1, 5, 7, 11, 15, 17],
      G2: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20],
      G3: [9, 19],
      G4: [3, 13],
    },
    '245-10': {
      G1: [2, 4, 6, 10, 14, 16, 18],
      G2: [1, 5, 7, 9, 11, 13, 15],
      G3: [3, 17],
      G4: [8, 12],
    },
    '245-12': {
      G1: [1, 5, 7, 11, 15, 17],
      G2: [2, 4, 6, 8, 10, 12, 14, 16, 18, 20],
      G3: [9, 19],
      G4: [3, 13],
    },
    '245-12x10': {
      G1: [2, 4, 6, 10, 14, 16, 18],
      G2: [1, 5, 7, 9, 11, 13, 15],
      G3: [3, 17],
      G4: [8, 12],
    },
    // 177 double halo — G1/G2 = inner, G3/G4 = outer (builder gem-map sheet)
    '177-10': {
      G1: [1, 3, 5, 7, 9, 11, 13],
      G2: [2, 4, 6, 8, 10, 12, 14],
      G3: [15, 17, 19, 21, 23, 25, 27],
      G4: [16, 18, 20, 22, 24, 26, 28],
    },
    '177-12': {
      G1: [2, 4, 6, 8, 10, 12, 14, 16],
      G2: [1, 3, 5, 7, 9, 11, 13, 15],
      G3: [18, 20, 22, 24, 26, 28, 30, 32],
      G4: [17, 19, 21, 23, 25, 27, 29, 31],
    },
    '177-12x10': {
      G1: [2, 4, 6, 8, 10, 12, 14, 16],
      G2: [1, 3, 5, 7, 9, 11, 13, 15],
      G3: [18, 20, 22, 24, 26, 28, 30, 32],
      G4: [17, 19, 21, 23, 25, 27, 29, 31],
    },
  };

  /**
   * Style-size → which G slots get the primary accent for Alternating.
   * 193 10 + 12x10: G1+G4 color, G2+G3 white
   * 250 / 12mm: G2 color (G1+G3+G4 white)
   * 10mm (not 193): G2+G4 color, G1+G3 white
   * 310/311: no every-other; see isFourCornerX()
   */
  var ALT_ACCENT_GROUPS = {
    '123-10': [1, 3, 4],
    '123-12x10': [1, 3, 4],
    '123-12': [1, 3, 4],
    '245-12': [1, 3, 4],
    // 245-10: G2+G3 accent, G1+G4 white (per production)
    '245-10': [2, 3],
    '245-12x10': [2, 3],
    '193-10': [1, 4],
    '193-12x10': [1, 4],
    '193-12': [2],
    '250-10': [2, 4],
    '250-12': [2],
    '250-12x10': [2],
  };

  function normalizeTopCode(top) {
    return String(top || '').replace(/F$/i, '');
  }

  function sizeFromShape(shapeCode) {
    var s = String(shapeCode || '').toUpperCase();
    if (s === 'L') return '12x10';
    if (s === 'E') return '12';
    return '10';
  }

  function gemMapKey(topCode, shapeCode) {
    return normalizeTopCode(topCode) + '-' + sizeFromShape(shapeCode);
  }

  function isFourCornerX(topCode) {
    var code = normalizeTopCode(topCode);
    return code === '310' || code === '311';
  }

  function altAccentGroups(key) {
    if (ALT_ACCENT_GROUPS[key]) return ALT_ACCENT_GROUPS[key].slice();
    if (/-12x10$/.test(key) || /-12$/.test(key)) return [2];
    if (/-10$/.test(key)) return [2, 4];
    return [1, 3, 4];
  }

  function getGemMap(mapKey) {
    return GEM_MAPS[mapKey] || null;
  }

  function hasCornerGroups(mapKey) {
    var map = getGemMap(mapKey);
    if (!map) return false;
    return Boolean(
      (map.G3 && map.G3.length) || (map.G4 && map.G4.length)
    );
  }

  /**
   * Resolve map key + four-corner flag from either explicit mapKey or top+shape.
   * @param {object} [ctx]
   * @param {string} [ctx.mapKey]
   * @param {string} [ctx.topCode]
   * @param {string} [ctx.shapeCode] N|E|L
   * @param {boolean} [ctx.fourCornerX]
   */
  function resolveContext(ctx) {
    ctx = ctx || {};
    var key =
      ctx.mapKey ||
      gemMapKey(ctx.topCode, ctx.shapeCode);
    var four =
      typeof ctx.fourCornerX === 'boolean'
        ? ctx.fourCornerX
        : isFourCornerX(ctx.topCode || String(key).split('-')[0]);
    return { key: key, fourCornerX: four };
  }

  /**
   * Pattern kind → [G1, G2, G3, G4] gem color values.
   * @param {string} kind
   * @param {string} [colorA]
   * @param {string} [colorB]
   * @param {object} [ctx] mapKey and/or topCode + shapeCode
   * @returns {string[]}
   */
  function gemsForPattern(kind, colorA, colorB, ctx) {
    var resolved = resolveContext(ctx);
    var key = resolved.key;
    var white = 'white';
    var a = colorA || white;
    var b = colorB || white;

    if (kind === 'all_white') return [white, white, white, white];
    if (kind === 'all') return [a, a, a, a];

    // 310/311: four corner stones. Alternating = halves; Alternating X = opposite corners.
    if (resolved.fourCornerX) {
      if (kind === 'x') return [a, a, a, a];
      if (kind === 'alt_x') return [a, b, b, a];
      if (kind === 'alt') return [a, a, b, b];
    }

    // X: G3+G4 accent, G1+G2 white (single-halo corner styles — not 177)
    if (kind === 'x') return [white, white, a, a];

    // Alternating X: G3 = color A, G4 = color B, G1+G2 white
    if (kind === 'alt_x') return [white, white, a, b];

    // 177 double halo: G1/G2 = inner row, G3/G4 = outer row (Marvin stone_map)
    if (kind === 'all_inner') return [a, a, white, white];
    if (kind === 'all_outer') return [white, white, a, a];
    if (kind === 'alt_inner') return [a, white, white, white];
    if (kind === 'alt_inner_b') return [white, a, white, white];
    if (kind === 'alt_outer') return [white, white, a, white];
    if (kind === 'alt_outer_b') return [white, white, white, a];
    if (kind === 'alt_inner_2') return [a, b, white, white];
    if (kind === 'alt_outer_2') return [white, white, a, b];
    // Pinwheel = ALT INNER + ALT OUTER
    if (kind === 'pinwheel') return [a, white, a, white];
    if (kind === 'pinwheel_b') return [white, a, white, a];
    if (kind === 'pinwheel_inner_shift') return [white, a, a, white];
    if (kind === 'pinwheel_outer_shift') return [a, white, white, a];
    if (kind === 'pinwheel_2') return [a, white, b, white];
    if (kind === 'pinwheel_2b') return [white, a, white, b];
    if (kind === 'pinwheel_2_inner_shift') return [white, a, b, white];
    if (kind === 'pinwheel_2_outer_shift') return [a, white, white, b];
    if (kind === 'all_inner_outer') return [a, a, b, b];
    if (kind === 'alt_inner_all_outer') return [a, white, b, b];
    if (kind === 'all_inner_alt_outer') return [a, a, b, white];

    if (kind === 'alt') {
      var accentGroups = altAccentGroups(key);
      var gems = [b || white, b || white, b || white, b || white];
      accentGroups.forEach(function (n) {
        gems[n - 1] = a;
      });
      return gems;
    }

    return [white, white, white, white];
  }

  global.SjStoneMaps = {
    GEM_MAPS: GEM_MAPS,
    ALT_ACCENT_GROUPS: ALT_ACCENT_GROUPS,
    normalizeTopCode: normalizeTopCode,
    sizeFromShape: sizeFromShape,
    gemMapKey: gemMapKey,
    isFourCornerX: isFourCornerX,
    altAccentGroups: altAccentGroups,
    getGemMap: getGemMap,
    hasCornerGroups: hasCornerGroups,
    gemsForPattern: gemsForPattern,
  };
})(typeof window !== 'undefined' ? window : this);
